// The Day insights card in a calendar day's details, and the badge on a calendar day (dayInsights.js
// works them out). Each insight: what happened, what it cost, how often it has come back, and a
// rule to add to the trader's rules in one click.
import { useMemo, useState } from 'react';
import { dayInsights } from './dayInsights';
import { useNavigate } from 'react-router-dom';
import { addRoutineRule } from './routineRules';
import { api } from './api';
import { formatMoney } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const ICON = { cost: '!', watch: '!', info: 'i', clean: '✓' };
const INTRO_KEY = 'mm-day-insights-intro';
const introSeen = () => {
  try {
    return localStorage.getItem(INTRO_KEY) === 'seen';
  } catch {
    return false;
  }
};

export function DayInsightBadge({ entry }) {
  const { t } = useI18n();
  if (!entry || (!entry.count && !entry.clean)) return null;
  // Days with only good-to-know notes get no badge; reviewed days get a quiet grey check.
  if (entry.reviewed && !entry.clean) {
    const label = t('{n} mistakes, reviewed', { n: entry.count });
    return <span className="di-badge reviewed" title={label} aria-label={label}>✓</span>;
  }
  const label = entry.clean ? t('Clean day') : t(entry.count === 1 ? '{n} mistake' : '{n} mistakes', { n: entry.count });
  return <span className={`di-badge ${entry.level}`} title={label} aria-label={label}>{entry.clean ? '✓' : entry.count}</span>;
}

// day: the day's key (2026-10-27), for marking it reviewed.
export default function DayInsightsCard({ entry, date, day, preview, canAskMark }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [added, setAdded] = useState({});
  const [intro, setIntro] = useState(() => !introSeen());
  const [reviewing, setReviewing] = useState(false);
  const closeIntro = () => {
    setIntro(false);
    try {
      localStorage.setItem(INTRO_KEY, 'seen');
    } catch {
      // Shown again next time in private mode.
    }
  };
  if (!entry || !entry.items.length) return null;
  const firstRule = entry.items.find((it) => it.rule && (it.level === 'cost' || it.level === 'watch'));
  const dayKey = day || entry.day;
  async function markReviewed(on) {
    setReviewing(true);
    try {
      const saved = await api(`/notes/${dayKey}`, { method: 'PUT', body: { insightsReviewed: on } });
      window.dispatchEvent(new CustomEvent('mm-note-saved', { detail: saved && saved.date ? saved : { date: dayKey, insightsReviewedAt: on ? new Date().toISOString() : undefined } }));
    } catch (err) {
      window.alert(err.message);
    } finally {
      setReviewing(false);
    }
  }
  const mistakes = entry.items.filter((it) => it.level === 'cost' || it.level === 'watch');
  async function add(rule) {
    try {
      await addRoutineRule(rule);
      const next = { ...added, [rule]: true };
      setAdded(next);
      // Every mistake on the day now has its rule: the day counts as reviewed.
      if (!entry.reviewed && mistakes.length && mistakes.every((it) => !it.rule || next[it.rule])) markReviewed(true);
    } catch (err) {
      window.alert(err.message);
    }
  }
  return (
    <section className={`di-card${preview ? ' admin-preview-card' : ''}${entry.reviewed ? ' reviewed' : ''}`}>
      <div className="di-head">
        <b>✦ {t('Day insights')}{entry.count > 0 && ` · ${t(entry.count === 1 ? '{n} mistake' : '{n} mistakes', { n: entry.count })}`}</b>
        {preview ? <span className="admin-preview-badge">Admin only</span> : <span className="di-sub">{t('From your trades and notes')}</span>}
      </div>
      {intro && (
        <div className="di-intro">
          <span>{t('These come from your own trades: every habit that cost you money, what it cost, and how often it comes back. Add a rule and we’ll keep checking it, day after day, until the habit stops.')}</span>
          <button type="button" className="di-intro-ok" onClick={closeIntro}>{t('Got it')}</button>
        </div>
      )}
      {entry.reviewed && mistakes.length > 0 && (
        <div className="di-reviewed">
          <span>✓ {t('Reviewed. These mistakes still count toward your repeat mistakes until the habit stops.')}</span>
          <button type="button" className="link-button" disabled={reviewing} onClick={() => markReviewed(false)}>{t('Undo')}</button>
        </div>
      )}
      <div className="di-list">
        {entry.items.map((it) => (
          <div key={it.kind} className={`di-item ${it.level}`}>
            <span className="di-icon" aria-hidden="true">{ICON[it.level]}</span>
            <div className="di-body">
              <b>{it.title}{it.count > 1 && <span className="di-n"> · {t('{n} trades', { n: it.count })}</span>}</b>
              <p>{it.text}</p>
              {it.repeat && <span className="di-repeat">↻ {it.repeat}</span>}
              {it.rule && (it.level === 'cost' || it.level === 'watch') && (
                added[it.rule] ? <span className="di-added">✓ {t('Added to your rules')}</span>
                  : <button type="button" className="di-rule" onClick={() => add(it.rule)}>+ {t('Rule')}: {it.rule}</button>
              )}
            </div>
            {it.cost !== null && it.cost !== undefined ? <span className={`di-cost ${it.cost < 0 ? 'loss' : 'gain'}`}>{formatMoney(it.cost)}</span>
              : it.level === 'clean' ? <span className="di-cost gain">{formatMoney(it.net)}</span> : <span />}
          </div>
        ))}
      </div>
      {(firstRule || canAskMark || mistakes.length > 0) && (
        <div className="di-actions">
          {mistakes.length > 0 && !entry.reviewed && <button type="button" className="small di-review-btn" disabled={reviewing} onClick={() => markReviewed(true)}>✓ {t('Mark as reviewed')}</button>}
          {canAskMark && <button type="button" className="secondary small" onClick={() => navigate('/app/mark', { state: { ask: t('Look at my trading day on {date}. What went wrong, and what should I do differently next time?', { date }) } })}>{t('Ask MARK about this day')}</button>}
        </div>
      )}
    </section>
  );
}

// Dashboard widget: the latest trading day's insights (today when there are trades today).
export function LatestDayInsights({ trades, accounts = [], notes = {}, setup }) {
  const { t } = useI18n();
  const byDay = useMemo(() => dayInsights(trades, { accounts, notes }), [trades, accounts, notes]);
  const days = Object.keys(byDay).sort();
  const last = days[days.length - 1];
  if (!last) return <section className="dash-card dw-card"><h3 className="dw-title">{t('Day insights')}</h3><p className="sw-empty">{t('No trades yet.')}</p></section>;
  const date = new Date(`${last}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <div className="di-widget">
      <span className="di-widget-day">{t('Latest trading day: {date}', { date })}</span>
      <DayInsightsCard entry={byDay[last]} date={date} preview={setup && setup.preview} canAskMark={setup && setup.mark} />
    </div>
  );
}

// Insights → Behavior: each mistake that keeps coming back over the last 30 trading days, how many
// of the last 10 it showed up on, what it cost, and how long since it last happened. A habit that
// hasn't shown up for 5 trading days is marked as stopped.
export function RepeatMistakes({ trades, accounts = [], notes = {}, onAddRule }) {
  const { t } = useI18n();
  const rows = useMemo(() => {
    const byDay = dayInsights(trades, { accounts, notes });
    const days = Object.keys(byDay).sort().slice(-30);
    const last10 = new Set(days.slice(-10));
    const map = new Map();
    days.forEach((day, i) => {
      for (const it of byDay[day].items) {
        if (it.level !== 'cost' && it.level !== 'watch') continue;
        const r = map.get(it.kind) || { kind: it.kind, title: it.title, rule: it.rule, days: 0, recent: 0, cost: 0, hasCost: false, lastIdx: -1 };
        r.days += 1;
        if (last10.has(day)) r.recent += 1;
        if (typeof it.cost === 'number') { r.cost += it.cost; r.hasCost = true; }
        r.lastIdx = i;
        map.set(it.kind, r);
      }
    });
    return [...map.values()].map((r) => ({ ...r, since: days.length - 1 - r.lastIdx, window: days.length }))
      .sort((a, b) => b.recent - a.recent || a.cost - b.cost);
  }, [trades, accounts, notes]);
  if (!rows.length) return null;
  return (
    <section className="rm-card">
      <div className="di-head"><b>↻ {t('Your repeat mistakes')}</b><span className="di-sub">{t('Last {n} trading days', { n: rows[0].window })}</span></div>
      <p className="rm-intro">{t('Every habit that cost you, and how often it keeps coming back. We’ll keep calling each one out until it stops.')}</p>
      <ul className="rm-list">
        {rows.map((r) => {
          const stopped = r.since >= 5;
          return (
            <li key={r.kind} className={stopped ? 'stopped' : r.recent >= 3 ? 'hot' : ''}>
              <div className="rm-main">
                <b>{r.title}</b>
                <span className="rm-meta">
                  {stopped
                    ? <span className="gain">✓ {t('Stopped: {n} trading days without it', { n: r.since })}</span>
                    : <>{t('{n} of your last 10 trading days', { n: r.recent })} · {r.since === 0 ? t('last seen on your latest day') : t(r.since === 1 ? 'last seen 1 trading day ago' : 'last seen {n} trading days ago', { n: r.since })}</>}
                </span>
                <span className="rm-dots" aria-hidden="true">{Array.from({ length: 10 }, (_, k) => <i key={k} className={k < r.recent ? 'on' : ''} />)}</span>
              </div>
              <span className="rm-side">
                {r.hasCost && <b className={r.cost < 0 ? 'loss' : 'gain'} title={t('Net P&L of the trades with this habit')}>{formatMoney(r.cost)}</b>}
                <small>{t(r.days === 1 ? '{n} day' : '{n} days', { n: r.days })}</small>
                {!stopped && r.rule && onAddRule && <button type="button" className="di-rule" onClick={() => onAddRule(r.rule)}>+ {t('Rule')}</button>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
